import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthService } from '@auth0/auth0-angular';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { combineLatest, filter, take } from 'rxjs';
import { PortalAuthService } from 'src/app/core/auth/portal-auth.service';
import { isProfileMissingError, isTenantContextError } from 'src/app/core/auth/auth-errors';
import { UserApiService } from 'src/app/core/services/user-api.service';
import { PortalUser } from 'src/app/core/domain/portal-user.model';
import { AccountSettingsService } from 'src/app/core/services/account-settings.service';
import { PageTransitionService } from 'src/app/core/services/page-transition.service';
import { StepUpService } from 'src/app/core/services/step-up.service';
import { IonicModule } from '@ionic/angular';

@Component({
    selector: 'app-callback',
    template: `
    <ion-content class="ion-text-center">
      @if (!errorMessage) {
        <div class="callback-center">
          <ion-spinner name="crescent" color="primary"></ion-spinner>
          <p>{{ 'callback.completing' | translate }}</p>
        </div>
      }
      @if (errorMessage) {
        <div class="callback-center">
          <ion-icon name="alert-circle-outline" color="danger" style="font-size:3rem"></ion-icon>
          <p style="color:var(--ion-color-danger)">{{ errorMessage }}</p>
          @if (canRetryFetch) {
            <ion-button fill="outline" (click)="fetchMe()">{{ 'common.retry' | translate }}</ion-button>
          }
          @if (!canRetryFetch) {
            <ion-button fill="outline" (click)="retryLogin()">{{ 'callback.retry' | translate }}</ion-button>
          }
        </div>
      }
    </ion-content>
    `,
    styles: [`.callback-center { display:flex; flex-direction:column; align-items:center; justify-content:center; height:100%; gap:1rem; padding:2rem; text-align:center; }`],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IonicModule, TranslatePipe]
})
export class CallbackPage implements OnInit {
  errorMessage: string | null = null;
  /** True when the retry button should re-run fetchMe() directly (transient backend
   * failure) rather than force a full logout/re-login. */
  canRetryFetch = false;
  /** The sign-in ended without a session (e.g. cancelled at Auth0): "sign in again" starts a login, not a logout. */
  private notSignedIn = false;

  constructor(
    private auth0: AuthService,
    private portalAuth: PortalAuthService,
    private userApi: UserApiService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private accountSettings: AccountSettingsService,
    private pageTransition: PageTransitionService,
    private stepUp: StepUpService
  ) { }

  ngOnInit(): void {
    // Covers the callback with the transition overlay and a loading bar until the target page is up.
    this.pageTransition.beginLoginTransition();

    // If Auth0 itself returns an error (e.g. org membership required), handle
    // it here before the normal isLoading$ / isAuthenticated$ flow completes.
    this.auth0.error$.pipe(take(1)).subscribe(err => {
      if (err) {
        this.showError(err.message ?? this.translate.instant('callback.loginFailed'));
      }
    });

    combineLatest([this.auth0.isLoading$, this.auth0.isAuthenticated$]).pipe(
      filter(([loading, _]) => !loading),
      take(1)
    ).subscribe(([_, isAuthenticated]) => {
      if (!isAuthenticated) {
        this.onNotAuthenticated();
        return;
      }
      this.fetchMe();
    });
  }

  /**
   * No session after the redirect. Saying so with a way to sign in again beats the unauthorized page,
   * which tells a user with a working account that they lack permission. An Auth0 error that is
   * already displayed keeps its own text.
   */
  onNotAuthenticated(): void {
    if (this.errorMessage) return;
    this.notSignedIn = true;
    this.setRetryableError(this.translate.instant('callback.notSignedIn'), false);
  }

  /**
   * Fetches the current user profile and routes based on the result. Called once the
   * user is authenticated, and again directly from the retry button after a transient
   * (5xx) failure — without forcing a full logout/re-login and without re-running the
   * Auth0 redirect flow.
   */
  fetchMe(): void {
    this.errorMessage = null;
    this.canRetryFetch = false;
    this.notSignedIn = false;
    this.pageTransition.beginLoginTransition();
    this.cdr.markForCheck();

    this.userApi.getMe().subscribe({
      next: user => {
        this.portalAuth.setAuthContext(user.tenantId ?? null, user.role ?? '', user.isPlatformAdmin ?? false, user.defaultOrgId);
        this.accountSettings.sync(user);
        // Back from a step-up sign-in: the parked call is repeated first, then the user returns to where they were.
        // (Plain logins skip this and keep the synchronous routing below.)
        if (this.stepUp.hasPendingReturn()) {
          // The full-screen transition would hide the confirmation of the repeated call.
          this.pageTransition.endLoginTransition();
          void this.stepUp.resumeAfterReturn().then(target => {
            if (target) void this.router.navigateByUrl(target);
            else this.routeAfterLogin(user);
          });
          return;
        }
        this.routeAfterLogin(user);
      },
      error: (err: HttpErrorResponse) => {
        // 403 unknown_organization (org_id doesn't resolve to a tenant) or
        // missing_org_context (no org_id claim at all) — this is an existing user
        // whose token has no usable tenant, NOT a brand-new user. Route to the
        // dedicated no-tenant page instead of the onboarding/signup form.
        if (isTenantContextError(err)) {
          this.router.navigate(['/no-tenant']);
          return;
        }

        // 404 not_found — GetMyProfile returns this for every user until they've
        // completed registration (no UserProfileEntity row yet). This is the real
        // brand-new-user signal; route to onboarding just like the success path's
        // isNew check does for an already-registered-but-nameless profile.
        if (isProfileMissingError(err)) {
          this.router.navigate(['/onboarding']);
          return;
        }

        // Transient failure (backend down, rate limit, no connection): let the user retry the
        // same getMe() call directly instead of silently routing to onboarding or forcing a logout.
        if (err.status === 0) {
          this.setRetryableError(this.translate.instant('callback.noConnection'));
          return;
        }
        if (err.status >= 500 || err.status === 429) {
          this.setRetryableError(this.translate.instant('common.serverError'));
          return;
        }

        // Any other unexpected failure (e.g. expired/invalid token): fall back to a
        // full re-login rather than guessing this is a new-user case.
        this.setRetryableError(this.translate.instant('callback.loginFailed'), false);
      }
    });
  }

  private routeAfterLogin(user: PortalUser): void {
    // Platform admin → go directly to admin dashboard
    if (user.isPlatformAdmin) {
      this.router.navigate(['/admin/tenants']);
      return;
    }
    // New user with no profile yet → onboarding
    const isNew = !user.firstName && !user.lastName;
    this.router.navigate([isNew ? '/onboarding' : '/home']);
  }

  retryLogin(): void {
    if (this.notSignedIn) {
      void this.auth0.loginWithRedirect();
      return;
    }
    // Clear any stale Auth0 state, then redirect to login.
    this.auth0.logout({ logoutParams: { returnTo: `${window.location.origin}/home` } });
  }

  private setRetryableError(message: string, canRetryFetch = true): void {
    this.pageTransition.endLoginTransition();
    this.errorMessage = message;
    this.canRetryFetch = canRetryFetch;
    this.cdr.markForCheck();
  }

  private showError(message: string): void {
    this.pageTransition.endLoginTransition();
    if (message.includes('organization')) {
      this.errorMessage = this.translate.instant('callback.noOrgMessage');
    } else {
      this.errorMessage = message;
    }
    this.cdr.markForCheck();
  }
}
